import React, { useContext, useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import { FaBan, FaCheck, FaPlus, FaShoppingBag } from 'react-icons/all';
import { EditItem } from '../../components/Layout';
import { download } from '@/utils/download';
import QRCodeStyling from 'qr-code-styling';
import { LayoutStoreInstance } from '../../LayoutStore';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import i18n from '@/utils/i18n';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { IconButton } from '../../components/Button/Button';
import { useHistory } from 'react-router';
import { ContentSection } from '../../components/Layout/ContentSection';
import { getArrayBuffer } from '@/utils/bufferUtils';
import { useCreateSelfReturnCodesMutation } from '../../graphql/mutations/createSelfReturnCodes.generated';
import ExcelJS from 'exceljs';
import { Routes } from '@/utils/routes';

export interface SelfReturnId {
  id: string;
  isValid: boolean;
  storeId: string;
  imgSrc: string;
}

type SelfReturnQrCodeTabProps = {
  storeUuid: string;
  storeTextId: string;
  title: string;
};

export const SelfReturnQrCodeTab = (props: SelfReturnQrCodeTabProps) => {
  const history = useHistory();
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const [isLoading, setIsLoading] = useState(false);
  const [selfReturnIds, setSelfReturnIds] = useState<SelfReturnId[]>([]);
  const [amount, setAmount] = useState('0');
  const [selection, setSelection] = useState<SelfReturnId[]>([]);
  const [createSelfReturnCodesMutation] = useCreateSelfReturnCodesMutation();

  useEffect(() => {
    loadSelfReturnIds();
  }, []);

  async function loadSelfReturnIds() {
    try {
      const response = await Communicator.Get(
        '/merchant/GetSelfReturnIdsForStore',
        {
          storeId: props.storeUuid,
        },
      );
      const selfReturnIds = (await response.json()) as SelfReturnId[];
      for (const selfReturnId of selfReturnIds) {
        const qrCode = getSelfReturnQrCode(selfReturnId.id);
        const blob: any = await qrCode.getRawData('png');
        selfReturnId.imgSrc = window.URL.createObjectURL(blob);
      }
      setSelfReturnIds(
        [...selfReturnIds].sort((a, b) => a.id.localeCompare(b.id)),
      );
      setIsLoading(false);
    } catch (e) {
      console.log(JSON.stringify(e));
      LayoutStoreInstance.setError(i18n.t('SelfReturnQrCodesTab:failedToLoad'));
    }
  }

  function getSelfReturnQrCode(id: string) {
    return new QRCodeStyling({
      width: 230,
      data: `HTTP://VYT.TO/SR/${id}`,
      height: 230,
      image:
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjciIGhlaWdodD0iMjciIHZpZXdCb3g9IjAgMCAyNyAyNyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3LjEzNjYgMjEuODYzM0gyLjg3MzkzVjE3Ljg0MDlIMTcuMTM2NkMxOC43NzQ0IDE3Ljg0MDkgMjAuMTA3NSAxNi41Mzg2IDIwLjEwNzUgMTQuOTM3MkMyMC4xMDc1IDEzLjMzNTggMTguNzc1MSAxMi4wMzM2IDE3LjEzNjYgMTIuMDMzNkgxMC4yNTczVjguMDExNzhIMTcuMTM2NkMyMS4wNDM3IDguMDExNzggMjQuMjIyOSAxMS4xMTg1IDI0LjIyMjkgMTQuOTM3OUMyNC4yMjI5IDE4Ljc1NzIgMjEuMDQ0NCAyMS44NjM5IDE3LjEzNjYgMjEuODYzOVYyMS44NjMzWiIgZmlsbD0iIzFDMUEyMCIvPgo8cGF0aCBkPSJNMTEuNzU4MiAxNS4wMzcyTDIuODczOTMgMTAuMDIzTDExLjc1ODIgNS4wMDg4NVYxNS4wMzcyWiIgZmlsbD0iIzFDMUEyMCIvPgo8L3N2Zz4K',
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10,
      },
      backgroundOptions: {
        color: '#00000000',
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }

  // Creation goes through colugo, NOT vytal's /merchant/CreateSelfReturnIds.
  // Only colugo can mint the STORE_ASSET TrackableItem that makes a sticker
  // pickable, and it does so in the same transaction as the code itself — a
  // code created through the old endpoint exists but can never be scanned onto
  // a picking job.
  function createNewSelfReturnQrCodes(amount: number) {
    createSelfReturnCodesMutation({ variables: { amount } })
      .then(async (result) => {
        const selfReturnIds = (result.data?.createSelfReturnCodes ?? []).map(
          (code) => code.id,
        );
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');
        worksheet.properties.defaultRowHeight = 200;
        const promises = selfReturnIds.map(async (id) => {
          const qrCode = getSelfReturnQrCode(id);
          const blob = await qrCode.getRawData('png');
          const buf = await getArrayBuffer(blob);
          return workbook.addImage({
            buffer: buf,
            extension: 'png',
          });
        });
        const images = await Promise.all(promises);
        selfReturnIds.forEach((id) => worksheet.addRow([id]));
        worksheet.columns = [{ width: 35 }, { width: 32 }];
        images.forEach((i, index) =>
          worksheet.addImage(i, `B${index + 1}:B${index + 1}`),
        );
        workbook.xlsx.writeBuffer().then(function (buffer) {
          download(
            new Blob([buffer], { type: 'application/octet-stream' }),
            'selfReturnQrCodes.xlsx',
          );
        });
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
        LayoutStoreInstance.setError(
          i18n.t('SelfReturnQrCodesTab:failedToCreate'),
        );
      });
  }

  async function changeValidityOfSelectedReturnIds(setToValid: boolean) {
    setIsLoading(true);
    try {
      const url = setToValid
        ? '/merchant/ValidateSelfReturnIds'
        : '/merchant/InvalidateSelfReturnIds';
      await Communicator.PostData(
        url,
        selection.map((id) => id.id),
      );
      setSelection([]);
      await loadSelfReturnIds();
    } catch (e) {
      console.log(JSON.stringify(e));
      LayoutStoreInstance.setError(
        i18n.t('SelfReturnQrCodesTab:failedToInvalidate'),
      );
    }
  }

  return (
    <ContentSection title={props.title} collapsable={false} key="content">
      {allowedToAccess && (
        <EditItem
          title={i18n.t('Common:add')}
          helpText={i18n.t('SelfReturnQrCodesTab:addHelpText')}
        >
          <div>
            <IconButton
              type="button"
              label={i18n.t('Common:add')}
              icon={<FaPlus />}
              style={{ float: 'right' }}
              onClick={() => createNewSelfReturnQrCodes(parseInt(amount, 10))}
              disabled={isLoading || amount === '0'}
            />
            <br style={{ clear: 'both' }} />
            <div className="input-group mt-3 mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:amount')}
              </span>
              <input
                type="text"
                className="form-control"
                value={amount}
                onChange={(e) =>
                  setAmount(e.target.value.replace(/[^0-9]/g, ''))
                }
              />
            </div>
          </div>
        </EditItem>
      )}

      <EditItem
        title={i18n.t('SelfReturnQrCodesTab:selfReturnQrCodes')}
        helpText={i18n.t('SelfReturnQrCodesTab:tableHelp')}
      >
        <IconButton
          label={i18n.t('SelfReturnQrCodesTab:invalidate', {
            count: selection.length,
          })}
          icon={<FaBan />}
          style={{
            float: 'right',
            marginLeft: '0.4rem',
          }}
          onClick={() => changeValidityOfSelectedReturnIds(false)}
          disabled={isLoading || selection.length === 0}
          className={'mt-3 mb-3'}
        />
        <IconButton
          label={i18n.t('SelfReturnQrCodesTab:validate', {
            count: selection.length,
          })}
          icon={<FaCheck />}
          style={{ float: 'right' }}
          onClick={() => changeValidityOfSelectedReturnIds(true)}
          disabled={isLoading || selection.length === 0}
          className={'mt-3 mb-3'}
        />
        <IconButton
          label={i18n.t('SelfReturnQrCodesTab:orderMore', {
            count: selection.length,
          })}
          icon={<FaShoppingBag />}
          style={{ float: 'left' }}
          onClick={() => history.push(Routes.createOrder(props.storeUuid))}
          disabled={isLoading}
          className={'mt-3 mb-3'}
        />
        <br style={{ clear: 'both' }} />
        <DataTable
          value={selfReturnIds}
          dataKey="id"
          lazy
          loading={isLoading}
          selectionMode="multiple"
          totalRecords={selfReturnIds.length}
          className="p-datatable-striped"
          selection={selection}
          onSelectionChange={(e) => {
            setSelection(e.value);
          }}
        >
          <Column selectionMode="multiple" />
          <Column
            style={{ width: '30%' }}
            body={(id: SelfReturnId) => id.id}
            header={i18n.t('Common:id')}
          />
          <Column
            style={{ width: '30%' }}
            body={(id: SelfReturnId) => i18n.t(`Common:${id.isValid}`)}
            header={i18n.t('SelfReturnQrCodesTab:valid')}
          />
          <Column
            body={(id: SelfReturnId) => (
              <img
                style={{
                  height: 'auto',
                  width: 'auto',
                  maxWidth: '150px',
                  maxHeight: '150px',
                }}
                src={id.imgSrc}
                alt={id.id}
              ></img>
            )}
            header={i18n.t('Common:qrCode')}
          />
        </DataTable>
      </EditItem>
    </ContentSection>
  );
};
